<app-base-modal [open]="open" title="Audit Trail" size="full" (openChange)="close()">
  <div class="audit-trail-container">
    <div class="flex items-center justify-between mb-4">
      <span class="text-sm text-gray-500">{{ totalItems() }} entries</span>
      <button
        (click)="exportCsv()"
        [disabled]="exportingCsv() || entries().length === 0"
        class="btn-small-primary disabled:cursor-not-allowed disabled:opacity-40"
      >
        @if (exportingCsv()) {
          Exporting…
        } @else {
          Export CSV
        }
      </button>
    </div>

    <!-- Filter Bar -->
    <div
      class="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end md:gap-3 mb-4 p-3 bg-gray-50 rounded-lg"
    >
      <!-- Actor searchable dropdown -->
      @if (isPI()) {
        <div class="flex flex-col gap-1 md:w-52 relative">
          <label class="input-label" for="audit-actor">Actor</label>
          <div class="relative">
            <input
              id="audit-actor"
              type="text"
              class="input-field pr-7"
              placeholder="Search members…"
              [ngModel]="actorSearchText"
              (ngModelChange)="onActorInputChange($event)"
              (focus)="onActorFocus()"
              (blur)="onActorBlur()"
            />
            @if (filter.actorId) {
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                (mousedown)="$event.preventDefault()"
                (click)="clearActor()"
              >
                ×
              </button>
            }
          </div>
          @if (showActorDropdown) {
            <ul
              class="absolute top-full left-0 right-0 mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto"
            >
              @if (filteredMembers.length === 0) {
                <li class="px-3 py-2 text-sm text-gray-500">No members found</li>
              } @else {
                @for (member of filteredMembers; track member.id) {
                  <li
                    (mousedown)="$event.preventDefault()"
                    (click)="selectActor(member)"
                    class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
                    [class.bg-blue-50]="filter.actorId === '' + member.id"
                  >
                    <span class="font-medium">{{ getMemberDisplayName(member) }}</span>
                  </li>
                }
              }
            </ul>
          }
        </div>
      }

      <!-- Action dropdown -->
      <div class="flex flex-col gap-1 md:w-48">
        <label class="input-label" for="audit-action">Action</label>
        <select
          id="audit-action"
          [(ngModel)]="filter.action"
          (ngModelChange)="applyFilters()"
          class="select-field"
        >
          <option [ngValue]="undefined">All actions</option>
          <option value="PROJECT_MEMBER_ADD">Member added</option>
          <option value="PROJECT_MEMBER_REMOVE">Member removed</option>
          <option value="PROTOCOL_UPDATE">Protocol update</option>
          <option value="IMPORT_JOB_CREATE">Import job create</option>
          <option value="IMPORT_JOB_COMPLETE">Import job complete</option>
          <option value="IMPORT_JOB_FAIL">Import job fail</option>
          <option value="IMPORT_JOB_UNDO">Import job undo</option>
          <option value="PAPER_METADATA_UPDATE">Paper metadata update</option>
          <option value="STAGE_ADVANCED">Stage advanced</option>
          <option value="SCREENING_VOTE">Screening vote</option>
          <option value="UNDO_SCREENING_VOTE">Screening vote undo</option>
          <option value="FULL_TEXT_RETRIEVAL_DECIDE">Retrieval decision</option>
          <option value="FULL_TEXT_RETRIEVAL_UNDO">Retrieval decision undo</option>
          <option value="CONFLICT_RESOLVE">Conflict resolve</option>
          <option value="CLAIM_ANNOTATE">Claim annotate</option>
          <option value="CLAIM_DELETE">Claim delete</option>
          <option value="EXTRACTION_SUBMIT">Extraction submit</option>
          <option value="EXTRACTION_UNDO">Extraction undo</option>
        </select>
      </div>

      <!-- Stage dropdown -->
      <div class="flex flex-col gap-1 md:w-44">
        <label class="input-label" for="audit-stage">Stage</label>
        <select
          id="audit-stage"
          [(ngModel)]="filter.stage"
          (ngModelChange)="applyFilters()"
          class="select-field"
        >
          <option [ngValue]="undefined">All stages</option>
          @for (state of PROJECT_STATE_VALUES; track state) {
            <option [value]="state">{{ formatStage(state) }}</option>
          }
        </select>
      </div>

      <!-- Paper searchable dropdown -->
      <div class="flex flex-col gap-1 md:w-52 relative">
        <label class="input-label" for="audit-paper">Paper</label>
        <div class="relative">
          <input
            id="audit-paper"
            type="text"
            class="input-field pr-7"
            placeholder="Search papers…"
            [ngModel]="paperSearchText"
            (ngModelChange)="onPaperInputChange($event)"
            (focus)="onPaperFocus()"
            (blur)="onPaperBlur()"
          />
          @if (filter.entityId) {
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              (mousedown)="$event.preventDefault()"
              (click)="clearPaper()"
            >
              ×
            </button>
          }
        </div>
        @if (showPaperDropdown) {
          <ul
            class="absolute top-full left-0 right-0 mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto"
          >
            @if (paperSearchLoading()) {
              <li class="px-3 py-2 text-sm text-gray-500">Searching…</li>
            } @else if (paperSearchResults().length === 0 && paperSearchText.trim()) {
              <li class="px-3 py-2 text-sm text-gray-500">No papers found</li>
            } @else if (!paperSearchText.trim()) {
              <li class="px-3 py-2 text-sm text-gray-500">Start typing to search…</li>
            } @else {
              @for (paper of paperSearchResults(); track paper.paperToProjectId) {
                <li
                  (mousedown)="$event.preventDefault()"
                  (click)="selectPaper(paper)"
                  class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
                  [class.bg-blue-50]="filter.entityId === '' + paper.paperToProjectId"
                >
                  <span class="font-medium">{{
                    paper.title || 'Paper #' + paper.paperToProjectId
                  }}</span>
                </li>
              }
            }
          </ul>
        }
      </div>

      <!-- From date -->
      <div class="flex flex-col gap-1 md:w-36">
        <label class="input-label" for="audit-from">From</label>
        <input
          id="audit-from"
          type="date"
          [(ngModel)]="filter.from"
          (ngModelChange)="applyFilters()"
          class="input-field"
        />
      </div>

      <!-- To date -->
      <div class="flex flex-col gap-1 md:w-36">
        <label class="input-label" for="audit-to">To</label>
        <input
          id="audit-to"
          type="date"
          [(ngModel)]="filter.to"
          (ngModelChange)="applyFilters()"
          class="input-field"
        />
      </div>

      <!-- Clear button -->
      <div class="flex flex-col md:self-end">
        <button (click)="clearFilters()" class="btn-small-secondary min-h-[38px]">Clear</button>
      </div>
    </div>

    <!-- Entry List -->
    @if (loading() && entries().length === 0) {
      <div class="text-center py-8 text-gray-500">Loading...</div>
    } @else if (entries().length === 0) {
      <div class="text-center py-8 text-gray-500">No audit entries found.</div>
    } @else {
      <div class="entry-list space-y-2">
        @for (entry of entries(); track entry.id) {
          <div
            class="entry-card border-l-4 p-3 bg-white rounded-r-lg shadow-sm hover:shadow-md transition-shadow"
            [class]="getActionColor(entry.action)"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium text-sm">{{ entry.actorName }}</span>
                  <span class="text-xs text-gray-500">{{ entry.action | titlecase }}</span>
                  <span
                    class="text-xs px-1.5 py-0.5 rounded-full {{
                      getStageBadgeClass(entry.projectStage)
                    }}"
                  >
                    {{ formatStage(entry.projectStage) }}
                  </span>
                  @if (entry.entityId) {
                    <button
                      (click)="openPaperInfo(entry)"
                      class="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-900 hover:underline cursor-pointer transition-colors border-0"
                      title="View {{ entry.entityName || 'Paper #' + entry.entityId }} details"
                    >
                      {{ entry.entityName || 'Paper #' + entry.entityId }}
                    </button>
                  }
                </div>
                <p class="text-sm text-gray-800" [innerHTML]="entry.description"></p>
                <span class="text-xs text-gray-400">{{ formatTimestamp(entry.timestamp) }}</span>
              </div>
              @if (hasPayload(entry)) {
                <button
                  (click)="togglePayload(entry.id)"
                  class="text-xs text-blue-600 hover:text-blue-800 ml-2 shrink-0"
                >
                  {{ isPayloadExpanded(entry.id) ? 'Hide' : 'Details' }}
                </button>
              }
            </div>
            @if (isPayloadExpanded(entry.id)) {
              <div class="mt-2 p-2 bg-gray-50 rounded text-xs font-mono">
                <pre>{{ entry.payload | json }}</pre>
              </div>
            }
          </div>
        }
      </div>

      @if (currentPage() < totalPages() - 1) {
        <div class="text-center mt-4">
          <button
            (click)="loadMore()"
            [disabled]="loading()"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            @if (loading()) {
              Loading...
            } @else {
              Load more
            }
          </button>
        </div>
      }
    }

    <app-paper-info-modal
      [paperToProjectId]="selectedPaperId()"
      [projectId]="projectId()"
      (closed)="onPaperModalClosed()"
      (viewAuditTrail)="onPaperInfoViewAuditTrail($event)"
    ></app-paper-info-modal>
  </div>
</app-base-modal>
